import { useEffect, type ReactNode } from "react"

import {
  brandingLogoUrl,
  useBrandingQuery,
  useDocumentTitle,
  useFavicon,
} from "@/features/branding"
import { applyAccent } from "@/lib/theme/accent"
import { applyAction } from "@/lib/theme/action"

/**
 * Applies the deployment's branding app-wide.
 *
 * Mounted above the router (and therefore above auth) on purpose: the
 * login screen has no session, so branding must resolve without one.
 * Fetching here also means every consumer downstream reads the same
 * cached record rather than each firing its own request.
 *
 * Renders children unconditionally — if `/branding` is unreachable the
 * query resolves to a neutral record, because branding failing must never
 * stop a customer signing in.
 */
export function BrandProvider({ children }: { children: ReactNode }) {
  const { data } = useBrandingQuery()

  // The company's brand colour, as set by an admin in Company settings,
  // drives the primary buttons and the signal family (active marker, focus
  // rings). None set: blue buttons and the design's yellow markers.
  const brandColor = data?.primary_color ?? null
  useEffect(() => {
    applyAction(brandColor)
    applyAccent(brandColor)
  }, [brandColor])
  useDocumentTitle(data?.name, "Portal")
  // Square asset for a square slot; null when nothing is uploaded, which
  // leaves the shipped favicon alone rather than pointing at a 404.
  // Versioned, so a newly uploaded logo replaces the cached one.
  useFavicon(data?.has_logo ? brandingLogoUrl(data.asset_version) : null)

  return <>{children}</>
}
